<template>
    <div :class="[backgroundClasses, textColorClass, borderVerticalClasses]">
        <div
            class="flex flex-row items-center pl-2 pr-1 py-px font-light gap-2 shrink-0 text-[0.8125rem]/none"
            :class="[borderRightClasses]"
            :style="{ minHeight: `${heightInRem}rem` }"
            @mouseover="hoverTimelinePart(props.rowId, null, hasUnresolvedConflicts)"
        >
            <Tooltip v-if="checkbox.status !== 'No-Selection'">
                <template #trigger="tooltipProps">
                    <input
                        ref="checkboxRef"
                        :aria-labelledby="labelId"
                        :aria-describedby="tooltipProps['aria-labelledby']"
                        :popovertarget="tooltipProps['popovertarget']"
                        :checked="isChecked"
                        :aria-disabled="isDisabled"
                        type="checkbox"
                        class="size-4.5 rounded-sm border-gray-300 text-sky-700 cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:z-40 focus:ring-gray-700 hover:border-gray-700 group-hover:hover:border-gray-900 group-hover:border-gray-400 aria-disabled:focus-visible:ring-gray-500 aria-disabled:cursor-not-allowed aria-disabled:border-gray-300 aria-disabled:hover:border-gray-300"
                        :class="[
                            {
                                'aria-disabled:bg-gray-400': checkbox.status === 'Disabled-Selected',
                                'aria-disabled:bg-gray-200': checkbox.status === 'Disabled-Not-Selected',
                            },
                        ]"
                        @click="handleClick"
                        @change="handleToggle"
                    />
                </template>

                <template #text>
                    {{ checkboxTooltip }}
                </template>
            </Tooltip>

            <div
                class="flex flex-row items-center gap-2 flex-1 min-w-0"
                :style="{ paddingLeft: `calc(${props.level} * 1.625rem)` }"
            >
                <!-- Missing descendants: nothing to expand/collapse, so show the warning in place of the triangle. -->
                <Tooltip v-if="props.errorMessage">
                    <template #trigger="tooltipProps">
                        <WarningIcon
                            class="size-4.5 shrink-0 focus-visible:outline-2 rounded-xs cursor-help focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700"
                            v-bind="tooltipProps"
                            :tabindex="0"
                        />
                    </template>

                    <template #text>
                        <div class="w-max">{{ props.errorMessage }}</div>
                    </template>
                </Tooltip>

                <Tooltip v-if="props.expandable.isExpandable && !props.errorMessage" :is-teleported="true">
                    <template #trigger="tooltipProps">
                        <button
                            class="shrink-0 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs"
                            :class="
                                isExpandableDisabled
                                    ? 'cursor-not-allowed text-gray-400'
                                    : 'cursor-pointer hover:bg-gray-700 hover:text-gray-50 active:bg-gray-900 active:text-gray-50'
                            "
                            v-bind="tooltipProps"
                            :aria-expanded="props.expandable.isExpanded"
                            :aria-disabled="isExpandableDisabled"
                            type="button"
                            @click.stop="handleToggleExpanded"
                        >
                            <TriangleIcon
                                class="transition-transform rounded-xs size-4.5"
                                :class="{ 'rotate-90': props.expandable.isExpanded }"
                            />
                        </button>
                    </template>

                    <template #text>
                        <template v-if="triangleDisabledExplanation">
                            {{ triangleDisabledExplanation }}
                        </template>

                        <template v-else>
                            {{
                                props.expandable.isExpanded
                                    ? $t('collapse-timeline-location')
                                    : $t('expand-timeline-location')
                            }}
                        </template>
                    </template>
                </Tooltip>

                <div v-if="checkbox.status === 'No-Selection'" class="shrink-0 flex items-center justify-center">
                    <Tooltip>
                        <template #trigger="tooltipProps">
                            <Component
                                :is="props.icon.component"
                                class="size-4.5 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs cursor-help"
                                :tabindex="0"
                                v-bind="tooltipProps"
                            />
                        </template>

                        <template #text>
                            {{ props.icon.title }}
                        </template>
                    </Tooltip>
                </div>

                <div class="shrink-0 whitespace-nowrap tabular-nums">
                    <slot name="label" :label-id="labelId" :count-text-color-class="allocationCountTextColorClasses" />
                </div>

                <div
                    v-if="props.linkTarget || conflictsCount.total > 0"
                    class="flex flex-row items-center gap-2 ml-auto"
                >
                    <Tooltip v-if="conflictsCount.total > 0 && conflictsCount.unresolved === 0">
                        <template #trigger="tooltipProps">
                            <div class="flex items-center normal-case">
                                <FlashOffIcon
                                    class="focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs tabular-nums size-4 cursor-help"
                                    v-bind="tooltipProps"
                                    tabindex="0"
                                />
                            </div>
                        </template>

                        <template #text>
                            <div class="w-max">
                                <i18n path="all-conflicts-resolved-tooltip" :args="{ count: conflictsCount.total }">
                                    <template #conflictCount>
                                        <span class="font-semibold">
                                            {{ conflictsCount.total }}
                                        </span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </Tooltip>

                    <Tooltip v-if="hasUnresolvedConflicts">
                        <template #trigger="tooltipProps">
                            <div
                                class="flex flex-row gap-0.5 text-xs font-semibold items-center focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:z-40 focus-visible:outline-gray-700 rounded-xs tabular-nums cursor-help ml-0.75"
                                :class="[conflictsIndicatorClasses]"
                                v-bind="tooltipProps"
                                tabindex="0"
                            >
                                <span>
                                    {{ conflictsCount.unresolved }}
                                </span>

                                <FlashIcon class="size-4" />
                            </div>
                        </template>

                        <template #text>
                            <div class="w-max">
                                <i18n path="unresolved-conflicts-tooltip" :args="{ count: conflictsCount.unresolved }">
                                    <template #unresolvedCount>
                                        <span class="font-semibold tabular-nums">
                                            {{ conflictsCount.unresolved }}
                                        </span>
                                    </template>

                                    <template #totalCount>
                                        <span class="tabular-nums">
                                            {{ conflictsCount.total }}
                                        </span>
                                    </template>
                                </i18n>
                            </div>
                        </template>
                    </Tooltip>

                    <Tooltip v-if="props.linkTarget">
                        <template #trigger="tooltipProps">
                            <RouterLink
                                v-bind="tooltipProps"
                                target="_blank"
                                :to="props.linkTarget"
                                class="border size-4.5 flex justify-center items-center rounded-xs cursor-pointer focus-visible:outline-offset-2 focus-visible:outline-2 focus-visible:outline-gray-700 focus-visible:z-40"
                                :class="[
                                    locationLinkBackgroundColorClasses,
                                    locationLinkTextColorClasses,
                                    locationLinkBorderColorClasses,
                                ]"
                            >
                                <ExternalLinkIcon class="size-3" />
                            </RouterLink>
                        </template>

                        <template #header>
                            <slot name="link-tooltip-header" />
                        </template>

                        <template #text>
                            <slot name="link-tooltip-text" />
                        </template>
                    </Tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ExternalLinkIcon from '@/components/Icon/ExternalLinkIcon.vue';
import FlashIcon from '@/components/Icon/FlashIcon.vue';
import FlashOffIcon from '@/components/Icon/FlashOffIcon.vue';
import TriangleIcon from '@/components/Icon/TriangleIcon.vue';
import WarningIcon from '@/components/Icon/WarningIcon.vue';
import { triggerHideTooltipKey } from '@/components/Tooltip/injectionKeys';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import {
    type Component,
    computed,
    inject,
    onMounted,
    onUnmounted,
    provide,
    ref,
    useId,
    useTemplateRef,
    watchEffect,
} from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { useHeightSync } from '../composables/useHeightSync';
import { hoveredTimelinePartsKey, hoverTimelinePartKey } from '../injectionKeys';
import { type CheckboxState, useLocationSelectionStore } from '../LocationSelection.store.ts';
import type { LocationTypeCategory } from '../Timeline.types';

type AllocationFirstColumnCellProps = {
    locationId: string;
    parentId?: string;
    /** Passed to the selection store so a copy target's type travels with its id. */
    typeCategory: LocationTypeCategory;
    rowId: string;
    errorMessage?: string;
    expandable:
        | {
              isExpandable: true;
              isExpanded: boolean;
              nestedConflictsCount: {
                  resolved: number;
                  unresolved: number;
                  total: number;
              };
              /**
               * When set, this parent location is an ancestor of the selection source, so collapsing it
               * would hide the source: the expand/collapse triangle is disabled and shows this tooltip.
               */
              expandableDisabledExplanation?: string;
          }
        | {
              isExpandable: false;
              conflictsCount: {
                  resolved: number;
                  unresolved: number;
                  total: number;
              };
          };
    linkTarget?: RouteLocationRaw;
    level: number;
    icon: {
        component: Component;
        title: string;
    };
    /**
     * Whether the row is currently visible (all ancestors expanded). Hidden rows stay mounted for
     * selection registration but never need a checkbox, so we skip the selection-store lookup for them.
     * Optional (defaults to visible) so consumers that don't collapse rows keep working unchanged.
     */
    isVisible?: boolean;
};

const props = defineProps<AllocationFirstColumnCellProps>();

const emit = defineEmits<{
    (e: 'toggle-location-expanded'): void;
}>();

const { heightInRem } = useHeightSync(props.rowId);

const locationSelectionStore = useLocationSelectionStore();

// Registered in onMounted, not setup. The same location is rendered by both views with a *different*
// parent (a section in the sections view, a parent location in the hierarchy), and Vue runs unmounted hooks
// before mounted ones — so unregister-then-register is what re-links it to the right parent on a view
// switch. `checkbox` below covers the render-before-mount window.
onMounted(() => {
    locationSelectionStore.registerLocation(props.locationId, props.parentId ?? null, props.typeCategory);
});

onUnmounted(() => {
    locationSelectionStore.unregisterLocation(props.locationId);
});

const checkbox = computed<CheckboxState>(() => {
    // Hidden rows stay mounted for selection registration but never show a checkbox; skip the store lookup
    // so this computed stays independent of the selection store.
    if (props.isVisible === false) {
        return { status: 'No-Selection' };
    }
    // Not registered yet (first render, before onMounted) — no checkbox rather than a thrown guard.
    if (!locationSelectionStore.isLocationRegistered(props.locationId)) {
        return { status: 'No-Selection' };
    }
    return locationSelectionStore.getLocationCheckbox(props.locationId);
});

const checkboxTooltip = computed(() => {
    if (checkbox.value.status === 'No-Selection') {
        return '';
    }
    return checkbox.value.tooltip;
});

const labelId = useId();

const isDisabled = computed(
    () => checkbox.value.status === 'Disabled-Selected' || checkbox.value.status === 'Disabled-Not-Selected',
);

const isChecked = computed(() => checkbox.value.status === 'Selected' || checkbox.value.status === 'Disabled-Selected');

// When this expandable parent location is an ancestor of the selection source, collapsing it would hide
// the source, so the triangle is locked. (Only present on the `isExpandable: true` branch.)
const triangleDisabledExplanation = computed(() =>
    props.expandable.isExpandable ? props.expandable.expandableDisabledExplanation : undefined,
);
const isExpandableDisabled = computed(() => Boolean(triangleDisabledExplanation.value));

const handleToggleExpanded = () => {
    if (isExpandableDisabled.value) {
        return;
    }
    triggerHideTooltip();
    emit('toggle-location-expanded');
};

const checkboxRef = useTemplateRef('checkboxRef');
watchEffect(() => {
    if (checkboxRef.value) {
        checkboxRef.value.indeterminate = checkbox.value.status === 'Partially-Selected';
    }
});

function handleClick(event: MouseEvent) {
    triggerHideTooltip();

    // Prevent toggle for disabled items, stop propagation for all
    event.stopPropagation();
    if (isDisabled.value) {
        event.preventDefault();
    }
}

function handleToggle() {
    if (!isDisabled.value) {
        locationSelectionStore.toggleLocationSelection(props.locationId);
    }
}

const hoveredTimelineParts = inject(hoveredTimelinePartsKey);
if (!hoveredTimelineParts) {
    throw new Error('AllocationFirstColumnCell must be used within a Timeline component');
}

const hoverTimelinePart = inject(hoverTimelinePartKey);
if (!hoverTimelinePart) {
    throw new Error('AllocationFirstColumnCell must be used within a Timeline component');
}

const isRowHovered = computed(() => hoveredTimelineParts.rowId.value === props.rowId);
const isConflictCellHovered = computed(() => hoveredTimelineParts.isConflictCell.value);

const conflictsCount = computed(() =>
    props.expandable.isExpandable ? props.expandable.nestedConflictsCount : props.expandable.conflictsCount,
);
const hasUnresolvedConflicts = computed(() => conflictsCount.value.unresolved > 0);

const isExpandedParent = computed(() => props.expandable.isExpandable && props.expandable.isExpanded);

const backgroundClasses = computed(() => {
    if (isRowHovered.value) {
        if (isConflictCellHovered.value) {
            return 'bg-red-100';
        } else {
            return 'bg-blue-100';
        }
    }

    if (hasDarkConflictBackground.value) {
        return 'bg-red-700';
    }

    if (hasUnresolvedConflicts.value) {
        return 'bg-red-100';
    }

    return 'bg-gray-50';
});

/**
 * The one state with a dark background. Derived once and used by both the background and the text colour so
 * they cannot disagree: the error colour is itself dark red, so deciding the text before knowing the
 * background rendered dark red on dark red — invisible.
 */
const hasDarkConflictBackground = computed(
    () => !isRowHovered.value && hasUnresolvedConflicts.value && !isExpandedParent.value,
);

const textColorClass = computed(() => {
    // Legibility wins over the error tint. The warning icon and its tooltip still convey the error.
    if (hasDarkConflictBackground.value) {
        return 'text-red-50';
    }

    if (props.errorMessage) {
        return 'text-red-700';
    }

    return 'text-gray-900';
});

const borderVerticalClasses = computed(() => {
    if (isRowHovered.value) {
        if (isConflictCellHovered.value) {
            return 'border-vertical border-vertical-red-200 border-vertical-z-30';
        } else {
            return 'border-vertical border-vertical-blue-200 border-vertical-z-30';
        }
    }

    if (hasUnresolvedConflicts.value) {
        return 'border-vertical border-vertical-red-200 border-vertical-z-20';
    }

    return 'border-vertical border-vertical-gray-200 border-vertical-z-10';
});

const borderRightClasses = computed(() => {
    if (isRowHovered.value) {
        if (isConflictCellHovered.value) {
            return 'border-right-2-max-height border-right-red-700';
        }

        return 'border-right-2-max-height border-right-blue-400';
    }

    if (hasUnresolvedConflicts.value) {
        return 'border-right-2 border-right-red-800';
    }
});

const conflictsIndicatorClasses = computed(() => {
    if (isRowHovered.value) {
        if (isConflictCellHovered.value) {
            return 'text-red-700';
        }

        return 'text-gray-700';
    }

    if (hasUnresolvedConflicts.value) {
        if (isExpandedParent.value) {
            return 'text-red-700';
        }
        return 'text-white';
    }
});

const locationLinkBackgroundColorClasses = computed(() => {
    return 'bg-gray-50 hover:bg-gray-700 active:bg-gray-900';
});

const locationLinkTextColorClasses = computed(() => {
    return 'text-gray-700 hover:text-gray-50 active:text-gray-50';
});

const locationLinkBorderColorClasses = computed(() => {
    if (isRowHovered.value) {
        if (isConflictCellHovered.value) {
            return 'border-red-300 hover:border-gray-800 active:border-gray-900';
        } else {
            return 'border-blue-300 hover:border-gray-800 active:border-gray-900';
        }
    }

    return 'border-gray-300 hover:border-gray-800 active:border-gray-900';
});

const allocationCountTextColorClasses = computed(() => {
    if (!isRowHovered.value && hasUnresolvedConflicts.value && !isExpandedParent.value) {
        return 'text-gray-200';
    }

    return 'text-gray-500';
});

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}
</script>

<style scoped>
.border-vertical,
.border-right {
    position: relative;
}

.border-vertical::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.border-vertical::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 1px;
}

.border-vertical-z-10::before,
.border-vertical-z-10::after {
    z-index: 10;
}

.border-vertical-z-20::before,
.border-vertical-z-20::after {
    z-index: 20;
}

.border-vertical-z-30::before,
.border-vertical-z-30::after {
    z-index: 30;
}

.border-vertical-gray-200::before,
.border-vertical-gray-200::after {
    background-color: var(--color-gray-200);
}

.border-vertical-blue-200::before,
.border-vertical-blue-200::after {
    background-color: var(--color-blue-200);
}

.border-vertical-red-200::before,
.border-vertical-red-200::after {
    background-color: var(--color-red-200);
}

.border-right-2::after {
    content: '';
    position: absolute;
    top: 0;
    right: -2px;
    width: 2px;
    height: 100%;
}

.border-right-2-max-height::after {
    content: '';
    position: absolute;
    top: -1px;
    right: -2px;
    width: 2px;
    height: calc(100% + 2px);
}

.border-right-blue-400::after {
    background-color: var(--color-blue-400);
}

.border-right-red-700::after {
    background-color: var(--color-red-700);
}

.border-right-red-800::after {
    background-color: var(--color-red-800);
}
</style>
